import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import Spinner from "../../../../components/Spinners";
import { SectionCard } from "./SectionCard";
import { useRunRecord } from "../hooks/useTrackingHealth";
import { fmtDateTime } from "../format";
import { checkLabel, VALIDATOR_LAYER_DISPLAY } from "../vocabulary";
import { hotelCopyContext } from "../problem-copy";
import type {
  CheckOutcome,
  ProblemId,
  RunCell,
  ValidatorLayer,
} from "../types";

/**
 * The end-to-end pipeline (census.html's prototype, live): the six stages a
 * guest's click walks — Ad click → Landing page → Analytics → The hop →
 * Booking engine → Conversion recorded — with a Validator run's check
 * outcomes settled onto them.
 *
 * The card renders a run RECORD (`GET runs/:runId`); the diagnose mutation
 * itself is owned by the page (HotelDetail), which passes down `pending`,
 * the `runId` to render, and bumps `animSignal` when a fresh run's record
 * is ready — the checks then settle one by one, census animation included.
 * (The diagnose endpoint returns only the run OUTCOME — runId + counts —
 * never the cells, so the record is always fetched by id.)
 */

// ─── Stages & the checkId → stage map ────────────────────────────────────────

type StageKey = "ad" | "landing" | "analytics" | "hop" | "engine" | "conversion";

const STAGES: Array<{ key: StageKey; name: string; sub: string }> = [
  { key: "ad", name: "Ad click", sub: "Google Ads" },
  { key: "landing", name: "Landing page", sub: "hotel website" },
  { key: "analytics", name: "Analytics", sub: "GA4 property" },
  { key: "hop", name: "The hop", sub: "website → booking engine" },
  { key: "engine", name: "Booking engine", sub: "purchase moment" },
  { key: "conversion", name: "Conversion recorded", sub: "Google Ads" },
];

/**
 * The two stages that describe a specific hotel's booking engine, named after
 * it where the run knows which one it is.
 *
 * This is a diagram of ONE guest's journey through ONE hotel, so "booking
 * engine" here is a placeholder the page can usually fill — and filling it is
 * what turns the picture from a generic explainer into this hotel's own. The
 * unnamed labels survive verbatim for a hotel with no engine credited.
 */
function stagesFor(engine: string | null): typeof STAGES {
  if (!engine) return STAGES;
  return STAGES.map((stage) =>
    stage.key === "hop"
      ? { ...stage, sub: `website → ${engine}` }
      : stage.key === "engine"
        ? { ...stage, name: engine, sub: "purchase moment" }
        : stage,
  );
}

/**
 * Every check id in `DEFAULT_VALIDATOR_CATALOG`, on the stage of the guest's
 * journey it judges. Swept against the catalog 2026-08-13: `p13.attribution`
 * was missing (it fell through to the problem-level fallback and rendered as
 * its raw id), and `p13.designation` / `p13.designated-action` were listed but
 * exist only in backend test fixtures.
 */
const CHECK_STAGE: Record<string, StageKey> = {
  "p13.auto-tagging": "ad",
  "p1.measurement-ids-live": "landing",
  "p1.our-container-live": "landing",
  "p14.reachability": "landing",
  "p9.template-diff": "landing",
  "p9.unpublished-changes": "landing",
  "p7.consent-detection": "landing",
  "p7.consent-beacon-diff": "landing",
  "p3.purchase-absence": "analytics",
  "p4.ga4-purchase-params": "analytics",
  "p10.stream-config": "analytics",
  "p11.event-schema-peers": "analytics",
  "p5.hop-gl": "hop",
  "p6.conversion-linker": "hop",
  "p2.chain-diff": "hop",
  "p16.route-corroboration": "hop",
  "p16.route-discovery": "hop",
  "p14.no-booking-route": "hop",
  "p1.entry-page-tags": "engine",
  "p8.frame-src": "engine",
  "p13.purchase-action-exists": "conversion",
  "p13.purchase-action-unique": "conversion",
  "p13.zero-default-value": "conversion",
  "p13.counting-type": "conversion",
  "p13.attribution": "conversion",
  "p13.primary-for-goal": "conversion",
  "p4.ads-conversion-values": "conversion",
  "p15.destination-map": "conversion",
};

/** A check the map has never met still lands on a sensible stage. */
const PROBLEM_STAGE: Record<ProblemId, StageKey> = {
  "1": "landing",
  "2": "hop",
  "3": "analytics",
  "4": "analytics",
  "5": "hop",
  "6": "hop",
  "7": "landing",
  "8": "engine",
  "9": "landing",
  "10": "analytics",
  "11": "analytics",
  "12": "engine",
  "13": "conversion",
  "14": "landing",
  "15": "conversion",
  "16": "hop",
  // ADR 0039 — the account not delivering clicks is the journey failing at
  // its first step. No check emits a cell for it; the entry is here so the
  // catalog and this map cannot drift apart.
  "17": "ad",
};

function stageOf(cell: RunCell): StageKey {
  return CHECK_STAGE[cell.checkId] ?? PROBLEM_STAGE[cell.problemId] ?? "engine";
}

const BROWSER_LAYERS: ValidatorLayer[] = ["probe", "replay", "walk"];

// ─── Outcome rendering ────────────────────────────────────────────────────────

const OUTCOME_ICON: Record<CheckOutcome, string> = {
  pass: "✓",
  finding: "✕",
  "not-checkable": "–",
  "not-applicable": "○",
  "not-run": "○",
};

const OUTCOME_ICON_CLASS: Record<CheckOutcome, string> = {
  pass: "bg-success-dark border-success-dark text-white",
  finding: "bg-danger-dark border-danger-dark text-white",
  "not-checkable":
    "border-dashed border-warning-dark text-warning-dark bg-warning-light",
  "not-applicable": "border-gray-300 text-gray-400 bg-white",
  "not-run": "border-dashed border-gray-300 text-gray-300 bg-white",
};

const OUTCOME_ROW_CLASS: Partial<Record<CheckOutcome, string>> = {
  finding: "bg-danger-light",
};

type StageTone = "idle" | "ok" | "finding" | "warn" | "running";

const STAGE_TONE_CLASS: Record<StageTone, string> = {
  idle: "border-dashed border-[#E9E9EB]",
  ok: "border-success-dark/60",
  finding: "border-danger-dark/70",
  warn: "border-warning-dark/70",
  running: "border-info-dark shadow-[0_0_0_3px_#DBEAFE]",
};

const STAGE_HEAD_TONE_CLASS: Record<StageTone, string> = {
  idle: "",
  ok: "bg-success-light/60",
  finding: "bg-danger-light/70",
  warn: "bg-warning-light/70",
  running: "bg-info-light",
};

function stageTone(cells: RunCell[]): StageTone {
  if (cells.length === 0) return "idle";
  if (cells.some((cell) => cell.outcome === "finding")) return "finding";
  if (cells.some((cell) => cell.outcome === "not-checkable")) return "warn";
  if (cells.every((cell) => cell.outcome === "not-run" || cell.outcome === "not-applicable"))
    return "idle";
  return "ok";
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

// ─── The card ────────────────────────────────────────────────────────────────

interface SequencedCell {
  cell: RunCell;
  stage: StageKey;
  /** Global settle order (stage by stage, run order within a stage). */
  index: number;
}

export function PipelineCard({
  runId,
  pending,
  animSignal = 0,
  onRunDiagnose,
}: {
  /** The run record to render — the fresh diagnose run when one just
   *  finished, the hotel's last run otherwise. */
  runId: string | null;
  /** A diagnose run is executing server-side (14–84s measured). */
  pending: boolean;
  /** Bumped when a fresh run's record is owed — settles the checks from 0. */
  animSignal?: number;
  onRunDiagnose: () => void;
}) {
  const runQuery = useRunRecord(runId ?? "");
  const run = runQuery.data ?? null;
  // Defensive: the record read is the only shape with cells; anything else
  // (an outcome payload, a partial) renders as "no checks", never a crash.
  const cells: RunCell[] = useMemo(
    () => (run && Array.isArray(run.cells) ? run.cells : []),
    [run],
  );

  /** The engine this hotel books through, from the run record's own hotel
   *  block — null before a run exists, where none is credited, and where
   *  several are: the diagram has ONE engine stage, so two names would have to
   *  share a box that means "the place the booking happens". The generic
   *  labels are right for both engines; a joined one is right for neither. */
  const stages = useMemo(
    () => stagesFor(hotelCopyContext(run?.hotel).engine),
    [run],
  );

  // Settle animation: `revealed` counts settled checks; MAX = static (all
  // settled, the initial page-load rendering).
  const [revealed, setRevealed] = useState(Number.MAX_SAFE_INTEGER);
  const [waitStage, setWaitStage] = useState(0);
  const timerRef = useRef<number | null>(null);
  const lastSignal = useRef(animSignal);

  // A fresh run just finished: settle its checks from zero (the record may
  // still be loading — the settle effect waits for the sequence).
  useEffect(() => {
    if (animSignal !== lastSignal.current) {
      lastSignal.current = animSignal;
      setRevealed(0);
    }
  }, [animSignal]);

  const sequence: SequencedCell[] = useMemo(() => {
    const byStage = new Map<StageKey, RunCell[]>(
      STAGES.map((stage) => [stage.key, []]),
    );
    for (const cell of cells) byStage.get(stageOf(cell))!.push(cell);
    const out: SequencedCell[] = [];
    for (const stage of STAGES) {
      for (const cell of byStage.get(stage.key)!) {
        out.push({ cell, stage: stage.key, index: out.length });
      }
    }
    return out;
  }, [cells]);

  // Busy covers both halves of a live run: the run executing server-side,
  // then the fresh record loading.
  const busy = pending || (animSignal > 0 && runId != null && runQuery.isLoading);

  // While busy: cycle a pulsing highlight through stages.
  useEffect(() => {
    if (!busy) return;
    const handle = window.setInterval(
      () => setWaitStage((stage) => (stage + 1) % STAGES.length),
      700,
    );
    return () => window.clearInterval(handle);
  }, [busy]);

  // The settle animation: one check at a time, browser layers slower (they
  // were slower in real life too).
  useEffect(() => {
    if (revealed >= sequence.length) return;
    const current = sequence[revealed];
    const delay = BROWSER_LAYERS.includes(current.cell.layer) ? 280 : 140;
    timerRef.current = window.setTimeout(
      () => setRevealed((count) => count + 1),
      delay,
    );
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [revealed, sequence]);

  const animating = revealed < sequence.length;
  const settled = !animating && !busy;

  const findings = cells.filter((cell) => cell.outcome === "finding").length;
  const notCheckable = cells.filter(
    (cell) => cell.outcome === "not-checkable",
  ).length;

  return (
    <SectionCard
      title="End-to-end pipeline"
      headerRight={
        <span className="flex items-center gap-2 flex-wrap justify-end">
          {/* No `replay animation` control. The settle animation exists to
              show a live run landing; replaying it on a stale record was a
              button that changed nothing about what the page says. */}
          <button
            onClick={onRunDiagnose}
            disabled={pending}
            className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-md hover:bg-brand-primary-hover disabled:opacity-60 flex items-center gap-2"
          >
            {pending && <Spinner className="w-3.5 h-3.5" />}
            {pending ? "Running…" : "Diagnose now"}
          </button>
        </span>
      }
    >
      <p className="text-xs text-content-secondary mb-3">
        {busy ? (
          <>
            Running the full Validator — five layers, a live browser session;
            this can take a minute. The checks settle in as soon as the run
            record lands.
          </>
        ) : run ? (
          <>
            Validator ran {fmtDateTime(run.startedAt)} · {run.trigger} trigger ·
            layers{" "}
            {/* Every PLANNED layer, with the ones that did not execute struck
                through (§5.4). Listing only what ran made a browser outage
                indistinguishable from a sweep that never planned the walk —
                both simply showed fewer names. */}
            {run.layersPlanned.map((layer, i) => {
              const ran = run.layersExecuted.includes(layer);
              return (
                <span key={layer}>
                  {i > 0 && " · "}
                  <span
                    className={ran ? "" : "line-through text-gray-400"}
                    data-tip={
                      ran
                        ? undefined
                        : "Planned but not executed — every check this layer owns is recorded not-run below, never as a pass"
                    }
                  >
                    {VALIDATOR_LAYER_DISPLAY[layer]}
                  </span>
                </span>
              );
            })}
          </>
        ) : (
          <>
            The Validator has not run on this hotel — its state derives from
            the health rules alone. Diagnose now runs all five layers and
            fills this pipeline in.
          </>
        )}
      </p>

      {/* A run that inspected nothing must never read as a clean one. This is
          the shape a Validator-locked hotel produces: the endpoint answers
          200, a run record exists, and not one check was executed. */}
      {run && !busy && run.runStatus !== "completed" && (
        <div className="mb-3 px-3 py-2 rounded-md bg-warning-light text-xs text-warning-dark">
          {run.runStatus === "skipped-validator-lock" ? (
            <>
              <strong>Nothing was inspected.</strong> This hotel has a Validator
              lock, so the run stopped before its first layer — the stages below
              are empty because nobody looked, not because everything is fine.
              Remove the Validator lock to diagnose it.
            </>
          ) : (
            <>
              <strong>This run did not complete</strong> — it is recorded as{" "}
              <code>{run.runStatus}</code>. Read the stages below as partial.
            </>
          )}
        </div>
      )}

      {/* Legend (census pipelegend) */}
      <div className="flex items-center gap-4 flex-wrap text-[10.5px] text-gray-400 mb-3">
        {(
          [
            ["pass", "pass"],
            ["finding", "finding"],
            ["not-checkable", "not-checkable — could not inspect"],
            ["not-applicable", "not applicable"],
            ["not-run", "not run"],
          ] as Array<[CheckOutcome, string]>
        ).map(([outcome, label]) => (
          <span key={outcome} className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span
              className={clsx(
                "inline-flex w-[13px] h-[13px] rounded-full border items-center justify-center text-[8.5px] font-bold",
                OUTCOME_ICON_CLASS[outcome],
              )}
            >
              {OUTCOME_ICON[outcome]}
            </span>
            {label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
          <span className="px-1 rounded bg-info-light text-info-dark font-bold text-[8.5px] tracking-wide">
            LIVE
          </span>
          live-browser evidence (probes / replay / walk)
        </span>
      </div>

      {/* The six stages — they share the full card width when it's there
          (flex-1), and the row scrolls sideways only when it isn't. */}
      <div className="flex items-stretch overflow-x-auto pb-2">
        {stages.map((stage, stageIndex) => {
          const stageCells = sequence.filter((entry) => entry.stage === stage.key);
          const allSettled =
            stageCells.length > 0 &&
            stageCells.every((entry) => entry.index < revealed);
          const isRunningStage = busy
            ? waitStage === stageIndex
            : animating && stageCells.some((entry) => entry.index === revealed);
          const tone: StageTone = busy
            ? isRunningStage
              ? "running"
              : "idle"
            : isRunningStage
              ? "running"
              : allSettled || (settled && stageCells.length > 0)
                ? stageTone(stageCells.map((entry) => entry.cell))
                : "idle";
          return (
            <Fragment key={stage.key}>
              {stageIndex > 0 && (
                <span
                  className={clsx(
                    "flex-none w-7 flex items-center justify-center text-[17px]",
                    tone === "finding"
                      ? "text-danger-dark"
                      : tone === "ok"
                        ? "text-success-dark"
                        : tone === "warn"
                          ? "text-warning-dark"
                          : "text-[#E9E9EB]",
                  )}
                >
                  →
                </span>
              )}
              <div
                className={clsx(
                  "flex-1 min-w-[230px] border-2 rounded-xl bg-white overflow-hidden transition-all duration-200",
                  STAGE_TONE_CLASS[tone],
                )}
              >
                <div
                  className={clsx(
                    "px-3 py-2 border-b border-[#E9E9EB] flex items-baseline gap-2",
                    STAGE_HEAD_TONE_CLASS[tone],
                  )}
                >
                  <span className="text-[10px] font-bold text-gray-400">
                    {stageIndex + 1}
                  </span>
                  <span>
                    <span className="block text-[12.5px] font-semibold text-content-primary">
                      {stage.name}
                    </span>
                    <span className="block text-[10.5px] text-gray-400">
                      {stage.sub}
                    </span>
                  </span>
                </div>
                <div className="px-2.5 py-2 flex flex-col gap-1.5">
                  {stageCells.length === 0 && (
                    <span className="text-[11px] text-gray-300 py-1">
                      {run ? "no checks in this run" : "not yet inspected"}
                    </span>
                  )}
                  {stageCells.map((entry) => (
                    <PipelineCheck
                      key={`${entry.cell.checkId}-${entry.cell.route ?? ""}-${entry.index}`}
                      cell={entry.cell}
                      state={
                        busy
                          ? "pending"
                          : entry.index < revealed
                            ? "settled"
                            : entry.index === revealed
                              ? "running"
                              : "pending"
                      }
                    />
                  ))}
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* Summary line (census pipesummary) */}
      {run && settled && (
        <p className="text-sm text-content-secondary mt-1.5">
          <span className="font-semibold text-content-primary">
            {cells.length} checks
          </span>{" "}
          ·{" "}
          <span
            className={clsx(
              "font-semibold",
              findings > 0 ? "text-danger-dark" : "text-content-primary",
            )}
          >
            {findings} finding{findings === 1 ? "" : "s"}
          </span>
          {notCheckable > 0 && <> · {notCheckable} not-checkable</>}
          {/* "Clean run" is a claim about what was LOOKED AT. With no cells
              nothing was looked at, and calling that clean is the one thing
              this system must never do. */}
          {cells.length === 0
            ? " — no checks were executed, so this run says nothing about the hotel."
            : findings > 0
              ? " — every finding opens or joins the hotel's single incident."
              : notCheckable > 0
                ? " — no findings, but some checks could not be answered."
                : " — clean run."}{" "}
          <Link
            to={`/tracking/health/runs/${run.id}`}
            className="text-brand-primary hover:text-brand-primary-hover font-medium"
          >
            Open full run record →
          </Link>
        </p>
      )}
    </SectionCard>
  );
}

function PipelineCheck({
  cell,
  state,
}: {
  cell: RunCell;
  state: "pending" | "running" | "settled";
}) {
  const label = checkLabel(cell.checkId);
  const isBrowser = BROWSER_LAYERS.includes(cell.layer);
  const detail = `${cell.route ? `${cell.route} — ` : ""}${cell.evidence}`;
  return (
    <div
      className={clsx(
        "flex gap-2 items-start rounded px-1.5 py-1 text-[11.5px] leading-snug transition-colors",
        state === "pending" && "opacity-50",
        state === "running" && "bg-info-light",
        state === "settled" && OUTCOME_ROW_CLASS[cell.outcome],
      )}
      data-tip={`${cell.checkId} · ${VALIDATOR_LAYER_DISPLAY[cell.layer]} layer · ${cell.outcome}\n${detail}`}
    >
      <span
        className={clsx(
          "flex-none inline-flex w-[15px] h-[15px] mt-0.5 rounded-full border items-center justify-center text-[9.5px] font-bold",
          state === "settled"
            ? OUTCOME_ICON_CLASS[cell.outcome]
            : state === "running"
              ? "border-info-dark text-info-dark animate-pulse"
              : "border-gray-200 text-gray-300",
        )}
      >
        {state === "settled" ? OUTCOME_ICON[cell.outcome] : "·"}
      </span>
      <span className="min-w-0">
        <span className="block font-medium text-content-primary [overflow-wrap:anywhere]">
          {label}
          {isBrowser && (
            <span className="ml-1 px-1 rounded bg-info-light text-info-dark font-bold text-[8.5px] tracking-wide align-[1px]">
              LIVE
            </span>
          )}
        </span>
        {state === "settled" && (
          <span className="block text-[10.5px] text-content-secondary [overflow-wrap:anywhere]">
            {truncate(detail, 130)}
          </span>
        )}
      </span>
    </div>
  );
}
